import { httpClient } from "@/lib/http";
import { RIDERS_ENDPOINTS, ADMIN_ENDPOINTS } from "@/config/api";
import type { ApiResponse } from "@/lib/http";
import type { ApprovalSubmission } from "@/features/approvals/types";
import type {
    RidersQueryParams,
    RidersResponse,
    RiderDetails,
    RiderBookingsResponse,
    RiderTripsResponse,
    RiderWalletResponse,
    RiderNinVerificationPayload,
    RiderNinVerificationResponse,
} from "../types";

// ============================================================================
// Request Types
// ============================================================================

type UpdateUserStatusPayload = {
    action: "activate" | "reactivate" | "deactivate" | "suspend" | "ban";
    reason?: string;
};

// ============================================================================
// Helper Functions
// ============================================================================

const buildQueryString = (params: RidersQueryParams): string => {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) queryParams.append("page", params.page.toString());
    if (params.size !== undefined) queryParams.append("size", params.size.toString());
    if (params.status) queryParams.append("status", params.status);
    if (params.search) queryParams.append("search", params.search);
    if (params.emailVerified !== undefined && params.emailVerified !== "")
        queryParams.append("emailVerified", params.emailVerified.toString());
    if (params.phoneVerified !== undefined && params.phoneVerified !== "")
        queryParams.append("phoneVerified", params.phoneVerified.toString());
    if (params.ninVerified !== undefined && params.ninVerified !== "")
        queryParams.append("ninVerified", params.ninVerified.toString());
    if (params.createdFrom) queryParams.append("createdFrom", params.createdFrom);
    if (params.createdTo) queryParams.append("createdTo", params.createdTo);

    return queryParams.toString();
};

// ============================================================================
// API Functions
// ============================================================================

export const getRiders = async (params: RidersQueryParams = {}) => {
    const queryString = buildQueryString(params);
    const url = queryString ? `${RIDERS_ENDPOINTS.LIST}?${queryString}` : RIDERS_ENDPOINTS.LIST;
    const { data } = await httpClient.get<ApiResponse<RidersResponse>>(url);
    return data;
};

export const getRiderDetails = async (userId: string) => {
    const { data } = await httpClient.get<ApiResponse<RiderDetails>>(
        RIDERS_ENDPOINTS.DETAILS(userId)
    );
    return data;
};

export const updateUserStatus = async (userId: string, payload: UpdateUserStatusPayload) => {
    const { data } = await httpClient.put<ApiResponse<ApprovalSubmission>>(
        ADMIN_ENDPOINTS.USER_STATUS(userId),
        payload
    );
    return data;
};

export const updateRiderNinVerification = async (
    riderId: string,
    payload: RiderNinVerificationPayload
) => {
    const formData = new FormData();
    formData.append("nin", payload.nin);
    formData.append("verified", String(payload.verified));
    formData.append("file", payload.file);

    if (payload.adminNotes && payload.adminNotes.trim()) {
        formData.append("adminNotes", payload.adminNotes.trim());
    }

    const { data } = await httpClient.put<ApiResponse<RiderNinVerificationResponse>>(
        RIDERS_ENDPOINTS.UPDATE_NIN(riderId),
        formData
    );
    return data;
};

export const revokeRiderVerification = async (riderId: string, reason: string) => {
    const { data } = await httpClient.post<ApiResponse<RiderNinVerificationResponse>>(
        RIDERS_ENDPOINTS.REVOKE_VERIFICATION(riderId),
        { reason: reason.trim() }
    );
    return data;
};

export const activateRider = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "activate", reason });
};

export const reactivateRider = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "reactivate", reason });
};

export const deactivateRider = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "deactivate", reason });
};

export const suspendRider = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "suspend", reason });
};

export const banRider = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "ban", reason });
};

export const deleteRider = async (userId: string, reason?: string) => {
    const { data } = await httpClient.delete<ApiResponse<{ message: string }>>(
        RIDERS_ENDPOINTS.DELETE(userId),
        { data: reason ? { reason } : undefined }
    );
    return data;
};

// ============================================================================
// Rider Bookings, Trips, Wallet
// ============================================================================

export const getRiderBookings = async (riderId: string, page = 0, size = 10) => {
    const { data } = await httpClient.get<ApiResponse<RiderBookingsResponse>>(
        `${RIDERS_ENDPOINTS.BOOKINGS(riderId)}?page=${page}&size=${size}`
    );
    return data;
};

export const getRiderTrips = async (riderId: string, page = 0, size = 10) => {
    const { data } = await httpClient.get<ApiResponse<RiderTripsResponse>>(
        `${RIDERS_ENDPOINTS.TRIPS(riderId)}?page=${page}&size=${size}`
    );
    return data;
};

export const getRiderWallet = async (riderId: string, page = 0, size = 10) => {
    const { data } = await httpClient.get<ApiResponse<RiderWalletResponse>>(
        `${RIDERS_ENDPOINTS.WALLET(riderId)}?page=${page}&size=${size}`
    );
    return data;
};
